import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MRPType = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('mrp_types');
  const [viewItem, setViewItem] = useState(null);
  const [mrpTypes, setMrpTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);

  useEffect(() => {
    loadMrpTypes();
  }, []);

  const loadMrpTypes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/mrp-types`);
      setMrpTypes(response.data || []);
    } catch (error) {
      console.error('Error loading MRP types:', error);
      toast.error('Failed to load MRP types');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (type = null) => {
    setEditingType(type);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      mrp_type_id: (formData.get('mrp_type_id') || '').toUpperCase(),
      description: formData.get('description') || '',
      mrp_procedure: formData.get('mrp_procedure') || ''
    };

    try {
      if (editingType) {
        await axios.put(`${BACKEND_URL}/api/mrp-types/${editingType.mrp_type_id}`, {
          description: data.description,
          mrp_procedure: data.mrp_procedure
        });
        toast.success('MRP Type updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/mrp-types`, data);
        toast.success('MRP Type created successfully');
      }
      setIsDialogOpen(false);
      loadMrpTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save MRP type');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">MRP Types</h1>
            <p className="text-sm text-gray-500 mt-1">Manage Material Requirements Planning types</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">MRP Types</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add MRP Type
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">MRP Type ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">MRP Procedure</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mrpTypes.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-gray-500">
                        No MRP types found. Click &quot;Add MRP Type&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    mrpTypes.map((type) => (
                      <tr key={type.mrp_type_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{type.mrp_type_id}</td>
                        <td className="py-3 px-4 text-sm">{type.description}</td>
                        <td className="py-3 px-4 text-sm">{type.mrp_procedure}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="mrp_types" onView={() => setViewItem(type)} onEdit={() => handleOpenDialog(type)} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingType ? 'Edit MRP Type' : 'Add MRP Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mrp_type_id">MRP Type ID * (2 characters)</Label>
                  <Input
                    id="mrp_type_id"
                    name="mrp_type_id"
                    defaultValue={editingType?.mrp_type_id || ''}
                    placeholder="e.g., PD, VB, ND"
                    maxLength={2}
                    minLength={2}
                    pattern="[A-Za-z0-9]{2}"
                    title="Must be exactly 2 alphanumeric characters"
                    disabled={!!editingType}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be exactly 2 alphanumeric characters (e.g., PD, VB, ND)</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingType?.description || ''}
                    placeholder="e.g., MRP"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>MRP Procedure *</Label>
                <Input
                  name="mrp_procedure"
                  defaultValue={editingType?.mrp_procedure || ''}
                  placeholder="e.g., Material Requirements Planning"
                  required
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingType ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default MRPType;
